<template>
    <ConfirmDialog group="deleteProjectItem" :closeOnEscape="true" :dismissableMask="true">
        <template #message="slotProps">
            <div class="flex w-full items-start gap-3">
                <i v-if="slotProps.message.icon" :class="['rg-confirm-icon', slotProps.message.icon]" aria-hidden="true"></i>
                <p style="white-space: pre-line; text-align: center; margin: 0">{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <ConfirmDialog group="approveRgConfirmation" :closeOnEscape="true">
        <template #message="slotProps">
            <div class="flex w-full">
                <p>{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <ConfirmDialog group="approveOpenProjectConfirmation" :closeOnEscape="true">
        <template #message="slotProps">
            <div class="flex w-full">
                <p>{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <MContainer class="overflow-auto w-full h-full overflow-y-auto" style="max-height: calc(100% - 1.25rem) !important; background: transparent">
        <DataView
            v-bind:layout="props.layoutProp"
            v-bind:value="multipleSelected ? [] : projects"
            class="w-full h-full"
            style="background: transparent; overflow-y: auto"
            v-bind:dataKey="'1'">
            <template v-slot:empty>
                <span v-if="multipleSelected" class="rg-multiple-selected-empty">
                    <div class="rg-multiple-selected-shell">
                        <div class="rg-multiple-selected-card opacity-60">
                            <div class="rg-multiple-selected-badge">
                                <i class="pi pi-check-circle"></i>
                                <span>{{ $t("web-rg.multipleSelection") ?? "Multi-select" }}</span>
                            </div>
                            <div class="rg-multiple-selected-body">
                                <div class="rg-multiple-selected-icon">
                                    <i class="pi pi-info-circle"></i>
                                </div>
                                <div class="rg-multiple-selected-copy">
                                    <p>{{ $t("web-rg.multipleSelectionModeActive") }}</p>
                                    <p>{{ $t("web-rg.exportAndDeleteActionsSupported") }}</p>
                                    <p>{{ $t("web-rg.disableMultipleSelectionToSelectIndividualPatientAndLoadProjects") }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </span>
                <span v-else-if="hasLegacyProject">
                    <div class="rg-multiple-selected-shell">
                        <div class="rg-multiple-selected-card opacity-60">
                            <div class="rg-multiple-selected-body pb-4 px-2">
                                <div class="rg-multiple-selected-icon">
                                    <i class="pi pi-info-circle"></i>
                                </div>
                                <div class="rg-multiple-selected-copy">
                                    <p>{{ $t("web-rg.noProjectsCompatibleWithRealguideWebForSelectedPatientYet") }}</p>
                                    <p>{{ $t("web-rg.viewAndManagePatientProjectsOnRealguideDesktop") }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </span>
                <span v-else-if="!isLoading">
                    {{ $t("web-rg.noDataToDisplayClickImportcreateButtonToUploadItems") }}
                </span>
                <RgProjectItemSkeleton v-else />
            </template>
            <!-- grid layout -->
            <template v-slot:grid="slotProps">
                <div class="flex flex-row-wrap w-full" style="background: transparent">
                    <div :class="getProjectItemClass(index)" v-for="(item, index) in slotProps.items" v-bind:key="index">
                        <RgProjectGridItemRedesign
                            v-bind:project="item"
                            v-on:reload-patient-store="emits('reload-patient-store')"
                            v-on:segmentationTriggered="emits('segmentationTriggered', $event)"
                            v-on:sendToWeblinkClicked="emits('sendToWeblinkClicked', $event)"
                            v-on:exportClicked="emits('exportClicked', $event)"
                            v-on:exportToAlfred="emits('exportToAlfred', $event)"
                            :exportToAlfred="props.exportToAlfred" />
                    </div>
                </div>
            </template>
            <template v-slot:list="slotProps">
                <div v-for="key in groupKeys" :key="key">
                    <!-- RG group: plain list -->
                    <template v-if="key === 'RG'">
                        <MContainerColumn class="mr-2 gap-8">
                            <RgProjectItem
                                v-for="item in grouped[key]"
                                :sharedWith="props.sharedWith"
                                :key="item.id"
                                :project="item"
                                :username="username"
                                :revisionIndex="-1"
                                :exportToAlfred="props.exportToAlfred"
                                v-model:multipleSelected="multipleSelected"
                                @segmentationTriggered="emits('segmentationTriggered', $event)"
                                @reload-patient-store="emits('reload-patient-store')"
                                @exportClicked="emits('exportClicked', $event)" />
                        </MContainerColumn>
                    </template>

                    <!-- All other groups: wrapped in a Panel -->
                    <template v-else>
                        <Panel
                            :header="$t('web-rg.panelHeader', { key })"
                            :class="['mb-8 mr-2', shouldRemovePanelTopMargin(key) ? '' : 'mt-6', multipleSelected ? 'opacity-50 pointer-events-none' : '']"
                            :collapsed="isCollapsed(key)"
                            @update:collapsed="(val) => setCollapsed(key, val)"
                            style="border: solid 1px color-mix(in srgb, var(--p-primary-color) 25%, transparent) !important;}">
                            <template #header>
                                <MContainerColumn class="w-full">
                                    <MContainerRow>
                                        <MContainer class="justify-start" style="border-bottom: 2px solid var(--p-primary-400)">
                                            <span v-if="key" class="flex gap-2 items-center cursor-pointer" @click="toggleCollapsed(key)">
                                                <i class="pi pi-clipboard"></i>
                                                <span>{{ key }}</span>
                                            </span>
                                        </MContainer>
                                        <MContainerGrow />
                                        <div class="rg-notify-btn">
                                            <MUButton
                                                :label="$t('shared.notifications')"
                                                :iconSrc="'/icons/ik_shared_04_128.png'"
                                                :outlined="true"
                                                :iconSrcHeight="'24px'"
                                                :iconSrcWidth="'24px'"
                                                severity="secondary"
                                                rounded
                                                text
                                                :disabled="loading"
                                                @click="onNotificationsClick(key)" />
                                            <span v-if="getUnreadNotificationsForGroup(key) > 0" class="rg-notify-badge">
                                                {{ formatUnreadNotifications(getUnreadNotificationsForGroup(key)) }}
                                            </span>
                                        </div>
                                        <Divider v-if="(grouped[key]?.length ?? 0) > 1" layout="vertical" class="rg-vl" />
                                        <MUButton
                                            v-if="(grouped[key]?.length ?? 0) > 1"
                                            class="rg-revisions-toggle-btn"
                                            :label="isCollapsed(key) ? $t('web-rg.showAllRevisions') : $t('web-rg.hideAllRevisions')"
                                            :icon="isCollapsed(key) ? 'pi pi-chevron-circle-down' : 'pi pi-chevron-circle-up'"
                                            :outlined="true"
                                            severity="secondary"
                                            rounded
                                            text
                                            :disabled="loading"
                                            @click="toggleCollapsed(key)" />
                                    </MContainerRow>
                                    <Divider v-if="isCollapsed(key)" />
                                    <!-- preview first item when collapsed -->
                                    <MContainerRow class="mt-2 mb-2">
                                        <RgProjectItem
                                            v-if="isCollapsed(key) && grouped[key]?.length"
                                            :revisionIndex="0"
                                            :project="grouped[key][0]"
                                            :sharedWith="props.sharedWith"
                                            :exportToAlfred="props.exportToAlfred"
                                            v-model:multipleSelected="multipleSelected"
                                            @segmentationTriggered="emits('segmentationTriggered', $event)"
                                            @reload-patient-store="emits('reload-patient-store')"
                                            @exportClicked="emits('exportClicked', $event)" />
                                    </MContainerRow>
                                </MContainerColumn>
                            </template>
                            <!-- full list when expanded -->
                            <MContainerColumn v-if="!isCollapsed(key)" class="pt-6 gap-6 mb-2">
                                <RgProjectItem
                                    v-for="(item, index) in grouped[key]"
                                    :revisionIndex="index"
                                    :key="item.id"
                                    :project="item"
                                    :sharedWith="props.sharedWith"
                                    :exportToAlfred="props.exportToAlfred"
                                    v-model:multipleSelected="multipleSelected"
                                    @segmentationTriggered="emits('segmentationTriggered', $event)"
                                    @reload-patient-store="emits('reload-patient-store')"
                                    @exportClicked="emits('exportClicked', $event)" />
                            </MContainerColumn>
                        </Panel>
                    </template>
                </div>
            </template>
        </DataView>
    </MContainer>
</template>
<script lang="ts" setup>
import { MContainer, MContainerRow, MContainerColumn, MContainerGrow, MUButton } from "@xlabmedical/medic-web-framework";
import { ProjectModel } from "@xlabmedical/implant-planner";
import RgProjectGridItemRedesign from "@/components/ProjectItem/RgProjectGridItemRedesign.vue";
import ConfirmDialog from "primevue/confirmdialog";
import RgProjectItemSkeleton from "./RgProjectItemSkeleton.vue";
import { useI18n } from "vue-i18n";
import { Share } from "@xlabmedical/medic-api";

interface Props {
    layoutProp?: "list" | "grid" | undefined;
    exportToAlfred?: boolean;
    loading?: boolean;
    username?: string;
    sharedWith?: Share[];
}

const { t } = useI18n();

const projects = defineModel<ProjectModel[]>("projects");

const props = withDefaults(defineProps<Props>(), { layoutProp: "list" });

const multipleSelected = defineModel<boolean>("multipleSelected", { default: false });
const hasLegacyProject = defineModel<boolean>("hasLegacyProject", { default: false });

const grouped = computed(() => {
    const list = projects.value ?? [];

    // group first
    const groups = list.reduce<Record<string, ProjectModel[]>>((acc, p) => {
        let key = "";

        if (p.weblinkOrderData?.productionCenterId) {
            key = `${p.weblinkOrderData?.productionCenterName ?? ""} ${t("weblink.orderID")}: ${p.weblinkOrderData?.caseNumber ?? ""}`.trim();
        } else {
            key = p.weblinkOrderData?.caseNumber ?? "RG";
        }
        (acc[key] ||= []).push(p);

        return acc;
    }, {});

    // then sort each group by lastModified descending
    for (const key in groups) {
        groups[key].sort((a, b) => {
            const aDate = new Date(a.lastModified).getTime();
            const bDate = new Date(b.lastModified).getTime();
            return bDate - aDate; // newest first
        });
    }

    return groups;
});

// stable, sorted group keys with RG last
const groupKeys = computed(() => {
    const keys = Object.keys(grouped.value);
    const nonRg = keys
        .filter((k) => k !== "RG")
        .sort((a, b) => {
            const aCase = Number(grouped.value[a]?.[0]?.weblinkOrderData?.caseNumber ?? 0);
            const bCase = Number(grouped.value[b]?.[0]?.weblinkOrderData?.caseNumber ?? 0);
            return bCase - aCase; // largest caseNumber first
        });
    const rg = keys.includes("RG") ? ["RG"] : [];

    return [...nonRg, ...rg];
});

// per-group collapsed state (true = collapsed)
const collapsedByKey = ref<Record<string, boolean>>({});

// helpers
const isCollapsed = (key: string) => {
    if (!(key in collapsedByKey.value)) collapsedByKey.value[key] = true; // default collapsed
    return collapsedByKey.value[key];
};
const setCollapsed = (key: string, val: boolean) => (collapsedByKey.value[key] = val);
const toggleCollapsed = (key: string) => setCollapsed(key, !isCollapsed(key));
const shouldRemovePanelTopMargin = (key: string) => {
    const keys = groupKeys.value;
    if (!keys.length) return false;
    if (!keys.includes("RG")) return true;
    if (keys[0] !== "RG") return false;
    const firstNonRg = keys.find((k) => k !== "RG");
    return key !== firstNonRg;
};

const getUnreadNotificationsValue = (value: unknown) => {
    if (typeof value === "number") return value;
    if (Array.isArray(value)) {
        return value.reduce((sum, item) => sum + (typeof item === "number" ? item : 0), 0);
    }
    if (value && typeof value === "object") {
        return Object.values(value as Record<string, unknown>).reduce((sum, item) => sum + (typeof item === "number" ? item : 0), 0);
    }
    return 0;
};

const getUnreadNotificationsForGroup = (key: string) => {
    const items = grouped.value[key] ?? [];
    return items.reduce((max, item) => Math.max(max, getUnreadNotificationsValue(item.weblinkOrderData?.unreadNotificationsPerChannel)), 0);
};

const formatUnreadNotifications = (count: number) => {
    return count > 99 ? "99+" : String(count);
};

const emits = defineEmits<{
    (e: "reload-patient-store"): void;
    (e: "segmentationTriggered", value: string): void;
    (e: "exportClicked", value: string): void;
    (e: "sendToWeblinkClicked", value: string): void;
    (e: "exportToAlfred", value: string): void;
    (e: "order-notifications-clicked", value: string): void;
}>();

const getOrderIdForGroup = (key: string) => {
    const items = grouped.value[key] ?? [];
    return items[0]?.weblinkOrderData?.caseNumber;
};

const onNotificationsClick = (key: string) => {
    const orderId = getOrderIdForGroup(key);
    if (orderId) emits("order-notifications-clicked", orderId);
};

const getProjectItemClass = (index: number) => {
    let baseClass = "project-card-width project-card-height";

    if (index % 3 !== 0) {
        baseClass += ` ml-4`;
    }
    if (index > 2) {
        // hack for vertical gap
        baseClass += ` -mt-1`;
    }
    return baseClass;
};

const isLoading = computed(() => (projects.value?.length ?? 0) > 0);
</script>
<style scoped>
:deep(.project-card-height::before) {
    opacity: 0.5;
}

:deep(.p-dataview-content) {
    background: transparent !important;
}

:deep(.project-list-container)::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--p-card-home-background); /* Use the desired background color */
    opacity: 0.5; /* Set the desired opacity */
    z-index: 0; /* Ensure the pseudo-element is behind the content */
}

.rg-confirm-icon {
    font-size: 2rem;
    line-height: 2.5rem;
    margin-top: 0.15rem;
}

div.background {
    background: red;
    background-size: cover;
    align-items: center;
    display: flex;
    justify-content: center;
}

div.transbox {
    backdrop-filter: blur(5px);
    padding: 20px;
    margin: 30px;
    font-weight: bold;
}

:deep(.p-dataview-empty-message) {
    @apply pt-2;
}

.rg-multiple-selected-empty {
    display: block;
    width: 100%;
    text-align: center;
    padding: 1rem 0 2rem;
}

.rg-multiple-selected-shell {
    width: 100%;
    max-width: 1160px;
    margin: 0 auto;
    padding: 0.75rem 1.5rem;
    display: flex;
    justify-content: center;
}

.rg-multiple-selected-card {
    position: relative;
    width: 100%;
    background: linear-gradient(135deg, rgba(36, 76, 170, 0.35), rgba(15, 26, 64, 0.55));
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.35),
        0 0 0 1px rgba(255, 255, 255, 0.05);
    border-radius: 22px;
    overflow: hidden;
    backdrop-filter: blur(14px) saturate(110%);
    padding: 1.4rem 1.6rem 1.2rem;
}

.rg-multiple-selected-card::before {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: 18px;
    pointer-events: none;
}

.rg-multiple-selected-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.9rem;
    background: linear-gradient(120deg, color-mix(in srgb, #4d8dff 70%, transparent), rgba(255, 255, 255, 0.08));
    color: #f4f7ff;
    font-weight: 600;
    border-radius: 999px;
    font-size: 0.95rem;
    letter-spacing: 0.01em;
    box-shadow: 0 10px 30px rgba(13, 68, 160, 0.45);
}

.rg-multiple-selected-badge i {
    color: #7ee8ff;
    font-size: 1rem;
}

.rg-multiple-selected-body {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem 1.25rem;
    align-items: center;
}

.rg-multiple-selected-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: radial-gradient(circle at 30% 30%, rgba(255, 210, 96, 0.22), transparent 55%), linear-gradient(145deg, rgba(255, 214, 102, 0.18), rgba(255, 214, 102, 0.08));
    border: 1px solid color-mix(in srgb, #ffd95f 65%, transparent);
    box-shadow: 0 10px 35px rgba(255, 204, 92, 0.18);
}

.rg-multiple-selected-icon i {
    color: #ffd95f;
    font-size: 2rem;
    filter: drop-shadow(0 6px 12px rgba(255, 217, 95, 0.35));
}

.rg-multiple-selected-copy {
    text-align: left;
    color: #f9e7a7;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 1.05rem;
    letter-spacing: 0.01em;
}

.rg-multiple-selected-copy p {
    margin: 0;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .rg-multiple-selected-card {
        padding: 1.2rem 1.25rem;
    }

    .rg-multiple-selected-body {
        grid-template-columns: 1fr;
        text-align: center;
    }

    .rg-multiple-selected-icon {
        margin: 0 auto;
    }

    .rg-multiple-selected-copy {
        text-align: center;
    }
}

.rg-notify-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.rg-notify-badge {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background: #ffffff;
    color: #000000;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 0px #111827;
    z-index: 1;
}

@media (max-width: 1199px) {
    :deep(.rg-revisions-toggle-btn > span.h-full) {
        display: none;
    }

    :deep(.rg-revisions-toggle-btn > i) {
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}
</style>
